import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Send, CheckCircle, AlertTriangle, Settings, Plus, Trash2, Star, Mail } from 'lucide-react';
import { emailService, EmailProvider } from 'api/services/email.service';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';

const blank: EmailProvider = { name: '', providerType: 'SES', usePlatformAccount: false, active: true, smtpUseTls: true };

const EmailProviderSettings: React.FC = () => {
  const navigate = useNavigate();
  const [providers, setProviders] = useState<EmailProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  // null = list view; otherwise the sender being added (no id) or edited (has id)
  const [editing, setEditing] = useState<EmailProvider | null>(null);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');

  const load = async () => {
    setLoading(true);
    const res = await emailService.listProviders();
    if (res.success) setProviders(res.data || []);
    else setError(res.message || 'Failed to load senders');
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const p = editing || blank;
  const isSmtp = (p.providerType || 'SES').toUpperCase() === 'SMTP';
  const secretSet = !!p.sesSecretKeySet;
  const smtpPasswordSet = !!p.smtpPasswordSet;
  const set = (patch: Partial<EmailProvider>) => setEditing({ ...(editing || blank), ...patch });

  const save = async () => {
    setError(null); setOk(null);
    if (!p.name || !p.name.trim()) return setError('Give this sender a name');
    setSaving(true);
    const res = p.id
      ? await emailService.updateProvider(p.id, p)
      : await emailService.createProvider(p);
    setSaving(false);
    if (res.success) { setOk('Saved'); setEditing(null); await load(); }
    else setError(res.message || 'Failed to save');
  };

  const remove = async (id: string) => {
    const res = await emailService.deleteProvider(id);
    if (res.success) await load(); else setError(res.message || 'Failed to delete');
  };
  const makeDefault = async (id: string) => {
    const res = await emailService.setDefaultProvider(id);
    if (res.success) await load(); else setError(res.message || 'Failed to set default');
  };
  const test = async () => {
    setError(null); setOk(null);
    if (!p.id) return setError('Save the sender before testing it');
    if (!testTo) return setError('Enter a recipient to test');
    const res = await emailService.testProvider(p.id, testTo);
    if (res.success) setOk(`Test email accepted (messageId ${res.data?.messageId || '—'})`);
    else setError(res.message || 'Test failed');
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <button onClick={() => navigate('/email/campaigns')}
        className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-indigo-600">
        <ArrowLeft className="w-4 h-4" /> Back to campaigns
      </button>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings className="w-6 h-6 text-indigo-600" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Email Senders</h1>
        </div>
        {!editing && (
          <button onClick={() => { setEditing({ ...blank }); setOk(null); setError(null); }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
            <Plus className="w-4 h-4" /> Add sender
          </button>
        )}
      </div>

      {error && <ErrorMessage message={error} />}
      {ok && (
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
          <CheckCircle className="w-4 h-4" /> {ok}
        </div>
      )}

      {!editing ? (
        providers.length === 0 ? (
          <div className="text-center py-16 text-gray-500 dark:text-gray-400">
            <Mail className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>No email senders yet. Add one to start sending campaigns.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {providers.map((sp) => (
              <div key={sp.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white">{sp.name}</span>
                    {sp.isDefault && (
                      <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded-full">
                        <Star className="w-3 h-3" /> Default
                      </span>
                    )}
                    <span className="text-xs text-gray-400">{sp.providerType}</span>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{sp.fromName ? `${sp.fromName} · ` : ''}{sp.fromEmail || 'no from-address'}</div>
                </div>
                <div className="flex items-center gap-1">
                  {!sp.isDefault && (
                    <button title="Set default" onClick={() => sp.id && makeDefault(sp.id)}
                      className="p-2 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg"><Star className="w-4 h-4" /></button>
                  )}
                  <button title="Edit" onClick={() => { setEditing({ ...sp, sesSecretKey: '', smtpPassword: '' }); setOk(null); setError(null); }}
                    className="px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg">Edit</button>
                  <button title="Delete" onClick={() => sp.id && remove(sp.id)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Sender name *"><input value={p.name || ''} onChange={(e) => set({ name: e.target.value })} className={cls} placeholder="Marketing (SES)" /></Field>
              <Field label="Provider">
                <select value={p.providerType || 'SES'} onChange={(e) => set({ providerType: e.target.value })} className={cls}>
                  <option value="SES">Amazon SES</option>
                  <option value="SMTP">SMTP server</option>
                </select>
              </Field>
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!p.isDefault} onChange={(e) => set({ isDefault: e.target.checked })} className="w-4 h-4 rounded" />
              <span className="text-sm text-gray-700 dark:text-gray-200">Make this the default sender</span>
            </label>

            {!isSmtp && (
              <>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={!!p.usePlatformAccount} onChange={(e) => set({ usePlatformAccount: e.target.checked })} className="w-4 h-4 rounded" />
                  <span className="text-sm text-gray-700 dark:text-gray-200">Send using the platform's AWS account (leave off to use your own SES account)</span>
                </label>
                <Field label="AWS region"><input value={p.sesRegion || ''} onChange={(e) => set({ sesRegion: e.target.value })} className={cls} placeholder="ap-south-1" /></Field>
                {!p.usePlatformAccount && (
                  <>
                    <Field label="SES access key"><input value={p.sesAccessKey || ''} onChange={(e) => set({ sesAccessKey: e.target.value })} className={cls} placeholder="AKIA…" /></Field>
                    <Field label={`SES secret key${secretSet ? ' (stored — leave blank to keep)' : ''}`}>
                      <input type="password" value={p.sesSecretKey || ''} onChange={(e) => set({ sesSecretKey: e.target.value })} className={cls} placeholder={secretSet ? '••••••••' : 'secret'} />
                    </Field>
                  </>
                )}
              </>
            )}

            {isSmtp && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="SMTP host"><input value={p.smtpHost || ''} onChange={(e) => set({ smtpHost: e.target.value })} className={cls} placeholder="smtp.yourprovider.com" /></Field>
                  <Field label="Port"><input type="number" value={p.smtpPort ?? ''} onChange={(e) => set({ smtpPort: e.target.value ? Number(e.target.value) : null })} className={cls} placeholder="587" /></Field>
                </div>
                <Field label="SMTP username"><input value={p.smtpUsername || ''} onChange={(e) => set({ smtpUsername: e.target.value })} className={cls} placeholder="apikey / user@domain" /></Field>
                <Field label={`SMTP password${smtpPasswordSet ? ' (stored — leave blank to keep)' : ''}`}>
                  <input type="password" value={p.smtpPassword || ''} onChange={(e) => set({ smtpPassword: e.target.value })} className={cls} placeholder={smtpPasswordSet ? '••••••••' : 'password'} />
                </Field>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={p.smtpUseTls !== false} onChange={(e) => set({ smtpUseTls: e.target.checked })} className="w-4 h-4 rounded" />
                  <span className="text-sm text-gray-700 dark:text-gray-200">Use STARTTLS (port 465 uses implicit TLS automatically)</span>
                </label>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="From email *"><input value={p.fromEmail || ''} onChange={(e) => set({ fromEmail: e.target.value })} className={cls} placeholder="hello@yourdomain.com" /></Field>
              <Field label="From name"><input value={p.fromName || ''} onChange={(e) => set({ fromName: e.target.value })} className={cls} placeholder="Your Company" /></Field>
            </div>
            <Field label="Reply-to (optional)"><input value={p.replyToEmail || ''} onChange={(e) => set({ replyToEmail: e.target.value })} className={cls} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Max send rate / second"><input type="number" value={p.maxSendRatePerSecond ?? ''} onChange={(e) => set({ maxSendRatePerSecond: e.target.value ? Number(e.target.value) : null })} className={cls} placeholder="account limit" /></Field>
              <Field label="Daily quota"><input type="number" value={p.dailyQuota ?? ''} onChange={(e) => set({ dailyQuota: e.target.value ? Number(e.target.value) : null })} className={cls} /></Field>
            </div>

            {!p.fromEmail && (
              <div className="flex items-center gap-2 text-sm text-orange-600">
                <AlertTriangle className="w-4 h-4" /> A verified from-address is required before sending.
              </div>
            )}

            <div className="flex justify-between pt-2">
              <button onClick={() => { setEditing(null); setOk(null); setError(null); }} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:underline">Cancel</button>
              <button onClick={save} disabled={saving} className="tl-btn tl-btn-primary disabled:opacity-50">
                <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          {p.id && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">Send a test email from this sender</h3>
              <div className="flex gap-2">
                <input value={testTo} onChange={(e) => setTestTo(e.target.value)} className={cls} placeholder="you@example.com" />
                <button onClick={test} className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap">
                  <Send className="w-4 h-4" /> Test
                </button>
              </div>
              {p.lastVerifiedAt && <p className="text-xs text-gray-400">Last verified: {new Date(p.lastVerifiedAt).toLocaleString()}</p>}
              {p.lastError && <p className="text-xs text-red-500">Last error: {p.lastError}</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const cls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none';
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block"><span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</span>{children}</label>
);

export default EmailProviderSettings;
